<script setup lang="ts">
import type { DataTableHeaders } from '@/plugins/vuetify'
import type MDBVegcoBucket from '@/types/vegco/bucket'
import { notifyError, to_wd_dd_MMMM_hh_mm, emptyMongoId, roundTo } from '@/utils/helpers'
import { getBucketBigType, unitésParBoîte } from '@/utils/helpers-vegco'

definePage({
  meta: {
    id: 'vgco.récolteparjour',
    title: 'Récoltes par jour',
    icon: 'mdi-sprout-outline',
    drawerIndex: 5,
    requiresAuth: true,
    requiresSiteAccess: 'vegco'
  },
})

type MDBVegcoBucketExtended = MDBVegcoBucket & {
  producteur: string
  publié: boolean
  nomProduit: string
  typePalette: string
  unités: number
  dateChargéF: string
  noCommandeVente: string
  client: string
  dateChargé: Date
  nombrePalette: number
  unitsParPalette: number
}

const theme = useTheme()
const storeU = useUtilisateurStore()
const storeVegco = useVegcoStore()
const isLoading = ref(false)
const newData = ref(false)

const sites = storeU.utilisateur.pagesListeNoire.includes('vgco.tous') ?
  [storeU.siteSélectionné.lettresDappel] : ['Tous', 'DLFL', 'FHVW', 'PHVW', 'JPLG']
const selectedSite = ref(storeU.siteSélectionné.lettresDappel)
const selectedDay = ref(new Date(storeU.journéeSélectionnée))
const buckets = ref([] as MDBVegcoBucketExtended[])
const headers: DataTableHeaders = [
  { title: 'Producteur', key: 'producteur', width: '40px' },
  { title: 'Nom produit', key: 'nomProduit', width: '160px' },
  { title: 'Code de produit', key: 'codeProduit', width: '60px' },
  { title: 'Type de palette', key: 'typePalette', width: '60px' },
  { title: 'Nb de palettes', key: 'nombrePalette', width: '60px'},
  { title: 'Nb unités / palette', key: 'unitsParPalette', width: '60px'},
  { title: 'Unités', key: 'unités', width: '60px'},
  { title: 'Heure shipping MF', key: 'dateChargé', width: '120px'},
  { title: 'No. commande d\'achat', key: 'noCommandeAchat', width: '60px'},
  { title: 'Client', key: 'client', width: '120px'},
];
const totalUnités = computed(() =>
  buckets.value.reduce((sum, b) => sum + (b.unités || 0), 0)
)
const totalPalettes = computed(() =>
  buckets.value.reduce((sum, b) => sum + (b.nombrePalette || 0), 0)
)

watch(selectedDay, (_) => getData())

async function getData() {
  try{
    if (isLoading.value) {
      return
    }
    isLoading.value = true

    storeU.journéeSélectionnée = selectedDay.value.toISOString()
    buckets.value = []

    if (selectedSite.value == 'VGCO') {
      selectedSite.value = 'Tous'
    }

    const siteCS = selectedSite.value == 'Tous' ? 'VGCO' : selectedSite.value
    const siteId = await storeU.getOrFetchSiteId(siteCS)

    await storeU.fetchVegcoProduitsIfNeeded()

    const minDate = new Date(selectedDay.value)
    minDate.setDate(minDate.getDate() - 5)
    const maxDate = new Date(selectedDay.value)
    maxDate.setDate(maxDate.getDate() + 5)
    await storeVegco.loadDaysMultipleCultures(minDate, maxDate, [
      'Iceberg / Pommée', 'Laitue Romaine', 'Laitue Feuille Verte',
      'Laitue Feuille Rouge'
    ])

    const bucketsFound = storeVegco.getStoredBuckets(selectedDay.value)
    for (const bucket of bucketsFound) {
      if (bucket.quantité > 0 &&
        bucket.siteId == siteId || bucket.siteGroupeId == siteId) {

        if (bucket.commandeVenteId == emptyMongoId()) {
          continue // Skip buckets without a vente command
        }

        // Avoid duplicates
        if (buckets.value.find(b2 => b2.id == bucket.id)) {
          continue
        }

        const b = { ...bucket } as MDBVegcoBucketExtended

        b.producteur = storeVegco.getSiteLettresDappelRenommé(bucket.siteId) ?? '?'
        b.publié = new Date(b.datePublication).getFullYear() > 2000

        const produit = storeU.getVegcoProduit(bucket.codeProduit)
        if (produit) {
          b.nomProduit = produit.description
          b.typePalette = produit.typePalette
          b.unités = roundTo(b.quantité / produit.quantité, 1)
        } else {
          const units = unitésParBoîte(bucket.culture)
          b.nomProduit = getBucketBigType(bucket)
          b.unités = roundTo(b.quantité / units, 1)
        }

        const commande = storeVegco.commandesStore.find(c => c.id == bucket.commandeVenteId)
        if (commande) {
          b.noCommandeVente = commande.noCommande
          b.client = commande.client
          b.dateChargé = new Date(commande.dateChargé)
          b.dateChargéF = to_wd_dd_MMMM_hh_mm(b.dateChargé)

          const détail = commande.détails.find(d => d.codeProduit == bucket.codeProduit)
          if (détail) {
            b.nombrePalette = b.quantité / détail.quantité * détail.nombrePalette
            const nbPaletteArrondi = roundTo(b.nombrePalette, 0)
            if (Math.abs(nbPaletteArrondi - b.nombrePalette) <= 0.1) {
              b.nombrePalette = nbPaletteArrondi
            }

            b.unitsParPalette = détail.nombrePalette == 0 ? 0 : roundTo(b.unités / b.nombrePalette, 1)
          }
        }

        buckets.value.push(b)
      }
    }

    newData.value = false
  }
  catch (e) {
    notifyError(e)
  }
  finally {
    isLoading.value = false
  }
}

const dialogBucket = useTemplateRef('dialogBucket')
async function openBucket(bucket: MDBVegcoBucket) {
  const b = storeVegco.bucketsStore.find(b => b.id == bucket.id)
  if (!b) {
    notifyError('Bucket introuvable')
    return
  }
  const modifs = await dialogBucket.value?.openBucketEdit(b, selectedDay.value)
  if (modifs)
    getData()
}

const intervalId = setInterval(async () => {
  if (await storeVegco.isRevisionChanged())
    newData.value = true
}, 3000)

onMounted(async () => getData())

onUnmounted(() => {
  if (intervalId)
    clearInterval(intervalId)
})
</script>

<template>
  <v-card class="mx-4 my-4 page-card" rounded="xl" :loading="isLoading">

    <!-- Page header -->
    <div class="page-header px-5 pt-5 pb-3">
      <div class="d-flex align-center justify-space-between flex-wrap" style="gap: 12px;">

        <!-- Left: title -->
        <div class="d-flex align-center" style="gap: 12px;">
          <v-avatar color="primary" variant="tonal" size="44" rounded="lg">
            <v-icon icon="mdi-sprout-outline" size="26"/>
          </v-avatar>
          <div>
            <div class="text-h6 font-weight-bold" style="line-height: 1.2;">Récoltes par jour</div>
            <div class="text-caption text-medium-emphasis">Buckets par date de chargement</div>
          </div>
        </div>

        <!-- Right: actions -->
        <div class="d-flex align-center" style="gap: 8px;">
          <span class="text-body-2 text-medium-emphasis">Attention : cette liste provient des buckets. Vérifiez les POs réels.</span>
          <v-btn v-if="newData" color="primary" variant="text" prepend-icon="mdi-refresh" @click="getData()">Recharger les données</v-btn>
        </div>
      </div>

      <div class="d-flex align-center flex-wrap mt-3" style="gap: 8px;">
        <DaySelector ref="daySelector" v-model="selectedDay"/>
        <v-spacer/>
        <v-select
          v-model="selectedSite"
          :items="sites"
          label="Site"
          density="compact" variant="outlined" rounded="lg" hide-details
          style="max-width: 300px;"
          @update:model-value="getData()"/>
      </div>
    </div>
    <v-divider/>

    <v-data-table
      :headers="headers" :items="buckets"
      :loading="isLoading" item-key="id"
      :sort-by="[{ key: 'dateChargé', order: 'asc' }]" :sort-desc="false"
      :items-per-page="-1"
      :hide-default-header="$vuetify.display.smAndDown">
      <template #item.dateChargé="props">
        <v-chip :text="props.item.dateChargéF"/>
      </template>
      <template #item.noCommandeAchat="props">
        <v-btn
          size="small" color="primary"
          class="ma-1" @click="openBucket(props.item)">
          {{ props.item.noCommandeAchat }}
        </v-btn>
        <span>
          <v-icon v-if="props.item.publié" small class="ml-1">mdi-account-circle</v-icon>
          <v-tooltip activator="parent" location="bottom">Publié</v-tooltip>
        </span>
      </template>
      <template v-if="$vuetify.display.smAndDown" #item="props">
        <tr>
          <td class="pt-2 pb-2">
            <v-container>
              <v-row>
                <v-col cols="8">
                  Producteur: {{ props.item.producteur }}<br/>
                  Nom produit: {{ props.item.nomProduit }}<br/>
                  Code de produit: {{ props.item.codeProduit }}<br/>
                  Type de palette: {{ props.item.typePalette }}<br/>
                  Nb unités / palette: {{ props.item.unitsParPalette }}<br/>
                  Nb de palettes: {{ props.item.nombrePalette }}<br/>
                  Unités: {{ props.item.unités }}<br/>
                  Heure shipping MF: {{ props.item.dateChargéF }}<br/>
                  # de commande d'achat: {{ props.item.noCommandeAchat }}<br/>
                  # de commande de vente: {{ props.item.noCommandeVente }}<br/>
                  Client: {{ props.item.client }}<br/>
                </v-col>
              </v-row>
            </v-container>
          </td>
        </tr>
      </template>
    </v-data-table>

    <v-sheet class="pa-2 d-flex justify-space-between" style="border-top: 1px solid rgba(0,0,0,0.12);">
      <div class="font-weight-bold">Total</div>
      <div class="d-flex">
        <div class="px-4 font-weight-bold">Palettes: {{ totalPalettes }}</div>
        <div class="px-4 font-weight-bold">Unités: {{ totalUnités }}</div>
      </div>
    </v-sheet>

  </v-card>

  <DialogBucket ref="dialogBucket"/>
</template>

<style scoped>
/* ── Page header ─────────────────────────── */
.page-header {
  background: v-bind('theme.global.name.value === "dark" ? "rgba(var(--v-theme-surface-variant), 0.25)" : "rgba(var(--v-theme-primary), 0.04)"');
}
</style>
